import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { FinanceModalHeaderComponent } from '../../shared/modal-header/finance-modal-header.component';

/** State the modal opens with and returns on Apply. */
export interface ShowByModalState {
  /** Canonical ids of every togglable column in their current user-chosen order. */
  order: string[];
  /** Subset of {@code order} that's currently visible. */
  visible: string[];
}

export interface ShowByModalData {
  initial: ShowByModalState;
  /** Display label for each column id. */
  labels: Record<string, string>;
}

/**
 * Show by modal — controls which of the 22 togglable Resource Info columns
 * are visible AND their relative order. Per Figma: a centred dialog with
 * the column list in a 2-column layout, each row carrying a drag handle
 * (Material's open_with icon) and a checkbox.
 *
 * Drag-reorder uses Angular CDK Drag-Drop. The list is one flat
 * cdkDropList — the 2-column visual is pure CSS grid (column-major). Reorder
 * only affects the togglable columns; the table's always-visible and sticky
 * columns sit at fixed canonical positions and don't move when the user
 * shuffles the togglable subset.
 *
 * Apply emits the final order + visibility set; Reset restores the canonical
 * order with everything visible; Cancel discards changes.
 */
@Component({
  selector: 'app-show-by-modal',
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatIconModule,
    FinanceModalHeaderComponent,
  ],
  templateUrl: './show-by-modal.component.html',
  styleUrl: './show-by-modal.component.scss',
})
export class ShowByModalComponent {
  /** Working copy of the column order — drag-drop mutates this in place. */
  order: string[];
  /** Working copy of the visibility set as a plain object for ngModel binding. */
  visibleMap: Record<string, boolean>;
  /** Canonical order snapshot for the Reset button. */
  private readonly canonicalOrder: string[];

  constructor(
    private readonly dialogRef: MatDialogRef<ShowByModalComponent, ShowByModalState | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: ShowByModalData,
  ) {
    this.order = [...data.initial.order];
    this.canonicalOrder = [...data.initial.order];
    const visibleSet = new Set(data.initial.visible);
    this.visibleMap = {};
    for (const id of data.initial.order) {
      this.visibleMap[id] = visibleSet.has(id);
    }
  }

  labelFor(id: string): string {
    return this.data.labels[id] || id;
  }

  onDrop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.order, event.previousIndex, event.currentIndex);
  }

  toggleVisible(id: string): void {
    this.visibleMap[id] = !this.visibleMap[id];
  }

  reset(): void {
    this.order = [...this.canonicalOrder];
    for (const id of this.canonicalOrder) {
      this.visibleMap[id] = true;
    }
  }

  apply(): void {
    this.dialogRef.close({
      order: [...this.order],
      visible: this.order.filter((id) => this.visibleMap[id]),
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
